import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonPage,
  IonContent,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText,
  IonRefresherContent,
  IonGrid,
  IonModal,
  useIonRouter,
  useIonAlert,
} from '@ionic/react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { mailOutline, createOutline, ellipsisVertical, chevronBack, closeOutline } from 'ionicons/icons';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import PositionedRefresher from '../components/PositionedRefresher';
import EmailCompose from './EmailCompose';
import ServiceOrderModal, { type ServiceOrderDraft } from '../components/ServiceOrderModal';
import PdfViewerNative from '../components/PdfViewerNative';
import Invoice from './Invoice';
import CreateInvoice from './CreateInvoice';
import {
  getServiceOrderById,
  cancelServiceOrder,
  confirmServiceOrder,
  deleteServiceOrder,
  cloneServiceOrder,
  createInvoiceFromServiceOrder,
  updateServiceOrder,
  type ZohoSalesOrder,
} from '../services/zohoServiceOrderService';
import styles from './Invoice.module.scss';
import menuStyles from './ServiceOrderDetail.module.scss';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';

interface ServiceOrderDetailProps {
  onClose?: () => void;
  salesorderId?: string;
}

const BUSINESS_FALLBACK = {
  name:    'R&S Maids Service',
  address: '22002 64th Avenue West, STE M9, Mountlake Terrace, WA 98043',
  phone:   '(425) 866-3424',
  email:   'support@rsmaidsservice.net',
  website: 'https://rsmaidsservice.com',
};

const STATUS_STYLE_MAP: Record<string, string> = {
  draft:     'statusDraft',
  open:      'statusSent',
  invoiced:  'statusPaid',
  void:      'statusVoid',
  cancelled: 'statusVoid',
};

const ServiceOrderDetail: React.FC<ServiceOrderDetailProps> = ({ onClose, salesorderId }) => {
  const { t: tr } = useTranslation();
  const router = useIonRouter();
  const [presentAlert] = useIonAlert();
  const [order, setOrder] = useState<ZohoSalesOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEmail, setShowEmail] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showInvoiceId, setShowInvoiceId] = useState<string | null>(null);
  const [showConvertToInvoice, setShowConvertToInvoice] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState<'preview' | 'download' | 'print' | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewDataUrl, setPdfPreviewDataUrl] = useState<string | null>(null);
  const [pdfPreviewError, setPdfPreviewError] = useState<string | null>(null);
  const [, setPdfZoom] = useState(100);
  const menuBtnRef = useRef<HTMLIonButtonElement>(null);
  const menuCardRef = useRef<HTMLDivElement>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggleRow = (i: number) => setExpandedRows(prev => {
    const next = new Set(prev);
    if (next.has(i)) { next.delete(i); } else { next.add(i); }
    return next;
  });

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const titleCardElementRef = useRef<HTMLDivElement>(null);
  const contentElementRef = useRef<HTMLIonGridElement>(null);
  const wasAtTop = useRef(true);
  const [refresherDisabled, setRefresherDisabled] = useState(false);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push('/tabs/billing');
    }
  }, [onClose, router]);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentElementRef, 66, applyProgress);

  const loadOrder = useCallback(async (background = false) => {
    if (!salesorderId) {
      setError(tr('pages.ServiceOrderDetail.noServiceOrderIdProvided'));
      setIsLoading(false);
      return;
    }
    try {
      if (!background) setIsLoading(true);
      setError(null);
      const data = await getServiceOrderById(salesorderId);
      setOrder(data);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : tr('pages.ServiceOrderDetail.failedToLoadServiceOrder'));
    } finally {
      if (!background) setIsLoading(false);
    }
  }, [salesorderId, tr]);

  useEffect(() => { Promise.resolve().then(() => loadOrder()); }, [loadOrder]);

  const handleCancelOrder = useCallback(() => {
    // salesorder_id (not reference_number, empty by design on a standalone
    // order) is what actually identifies the order — see handleEditConfirm's
    // comment for the full explanation of this pattern.
    if (!order?.salesorder_id) return;
    presentAlert({
      header: tr('pages.ServiceOrderDetail.cancelServiceOrder'),
      message: tr('pages.ServiceOrderDetail.thisWillVoidInZohoBooks', { v1: order.salesorder_number || 'this service order' }),
      cssClass: 'glass-alert',
      buttons: [
        { text: tr('pages.ServiceOrderDetail.keepOrder'), role: 'cancel' },
        {
          text: tr('pages.ServiceOrderDetail.cancelOrder'),
          role: 'destructive',
          handler: () => {
            setCancelling(true);
            cancelServiceOrder(order.reference_number, order.salesorder_id)
              .then(() => loadOrder())
              .catch((err) => console.error('[ServiceOrderDetail] cancel failed', err))
              .finally(() => setCancelling(false));
          },
        },
      ],
    });
  }, [order, presentAlert, loadOrder, tr]);

  const handleConfirmOrder = useCallback(() => {
    // salesorder_id (not reference_number, empty by design on a standalone
    // order) is what actually identifies the order — see handleEditConfirm's
    // comment for the full explanation of this pattern.
    if (!order?.salesorder_id) return;
    setConfirmingOrder(true);
    confirmServiceOrder(order.reference_number, order.salesorder_id)
      .then(() => loadOrder())
      .catch((err) => {
        console.error('[ServiceOrderDetail] confirm failed', err);
        presentAlert({
          header: tr('pages.ServiceOrderDetail.confirmFailed'),
          message: err instanceof Error ? err.message : tr('pages.ServiceOrderDetail.couldNotConfirmThisServiceOrder'),
          cssClass: 'glass-alert',
          buttons: ['OK'],
        });
      })
      .finally(() => setConfirmingOrder(false));
  }, [order, presentAlert, loadOrder, tr]);

  const handleDeleteOrder = useCallback(() => {
    if (!order?.salesorder_id) return;
    presentAlert({
      header: tr('pages.ServiceOrderDetail.deleteServiceOrder'),
      message: tr('pages.ServiceOrderDetail.thisWillPermanentlyDeleteFromZoho', { v1: order.salesorder_number || 'this service order' }),
      cssClass: 'glass-alert',
      buttons: [
        { text: tr('pages.ServiceOrderDetail.keepOrder'), role: 'cancel' },
        {
          text: tr('pages.ServiceOrderDetail.deletePermanently'),
          role: 'destructive',
          handler: () => {
            setDeleting(true);
            deleteServiceOrder(order.salesorder_id)
              .then(() => handleClose())
              .catch((err) => {
                console.error('[ServiceOrderDetail] delete failed', err);
                presentAlert({
                  header: tr('pages.ServiceOrderDetail.deleteFailed'),
                  message: err instanceof Error ? err.message : tr('pages.ServiceOrderDetail.couldNotDeleteThisServiceOrder'),
                  cssClass: 'glass-alert',
                  buttons: ['OK'],
                });
              })
              .finally(() => setDeleting(false));
          },
        },
      ],
    });
  }, [order, presentAlert, handleClose, tr]);

  const handleCloneOrder = useCallback(() => {
    if (!order?.salesorder_id) return;
    setCloning(true);
    cloneServiceOrder(order.salesorder_id)
      .then((cloned) => {
        presentAlert({
          header: tr('pages.ServiceOrderDetail.serviceOrderCloned'),
          message: cloned?.salesorder_number
            ? tr('pages.ServiceOrderDetail.createdANewDraft', { salesorder_number: cloned.salesorder_number })
            : tr('pages.ServiceOrderDetail.aNewDraftServiceOrderWas'),
          cssClass: 'glass-alert',
          buttons: [{ text: tr('pages.ServiceOrderDetail.ok'), handler: () => handleClose() }],
        });
      })
      .catch((err) => {
        console.error('[ServiceOrderDetail] clone failed', err);
        presentAlert({
          header: tr('pages.ServiceOrderDetail.cloneFailed'),
          message: err instanceof Error ? err.message : tr('pages.ServiceOrderDetail.couldNotCloneThisServiceOrder'),
          cssClass: 'glass-alert',
          buttons: ['OK'],
        });
      })
      .finally(() => setCloning(false));
  }, [order, presentAlert, handleClose, tr]);

  const handleCreateInstantInvoice = useCallback(() => {
    if (!order?.salesorder_id) return;
    setCreatingInvoice(true);
    createInvoiceFromServiceOrder(order.salesorder_id)
      .then(({ invoiceNumber }) => {
        presentAlert({
          header: tr('pages.ServiceOrderDetail.invoiceCreated'),
          message: invoiceNumber ? tr('pages.ServiceOrderDetail.created', { invoiceNumber }) : tr('pages.ServiceOrderDetail.invoiceCreatedFromThisServiceOrder'),
          cssClass: 'glass-alert',
          buttons: ['OK'],
        });
        loadOrder();
      })
      .catch((err) => {
        console.error('[ServiceOrderDetail] create instant invoice failed', err);
        presentAlert({
          header: tr('pages.ServiceOrderDetail.failedToCreateInvoice'),
          message: err instanceof Error ? err.message : tr('pages.ServiceOrderDetail.couldNotCreateAnInvoiceFrom'),
          cssClass: 'glass-alert',
          buttons: ['OK'],
        });
      })
      .finally(() => setCreatingInvoice(false));
  }, [order, presentAlert, loadOrder, tr]);

  // Unlike Create Instant Invoice (which creates the invoice immediately via
  // Zoho's fromsalesorder endpoint), Convert to Invoice just carries the SO's
  // customer/line items over into the normal Create Invoice form for review —
  // same prefill mechanism BookingDetails.tsx's "Create Invoice from Service
  // Order" already uses. The invoice is only actually created when the user
  // submits that form, not by this handler. CreateInvoice.tsx itself resolves
  // customerName against the real contact list (real avatar/zohoContactId)
  // once it loads, falling back to the search box pre-filled with the name.
  const handleConvertToInvoice = useCallback(() => {
    if (!order?.salesorder_id) return;
    setShowConvertToInvoice(true);
  }, [order]);

  const handleEditConfirm = useCallback((draft: ServiceOrderDraft) => {
    // Standalone orders (no real Wix booking) have an empty reference_number
    // by design — salesorder_id is what actually identifies the order now,
    // reference_number was blocking the save entirely for those orders
    // (this whole function returned before even attempting the API call).
    if (!order?.salesorder_id) return;
    setSavingEdit(true);
    updateServiceOrder({
      bookingId:    order.reference_number,
      contactName:  order.customer_name || 'Guest',
      serviceName:  '',
      soDate:       draft.soDate,
      paymentTerms: draft.paymentTerms,
      paymentTermsLabel: draft.paymentTermsLabel,
      isInclusiveTax:    draft.isInclusiveTax,
      pricelistId:  draft.pricelistId,
      terms:        draft.terms,
      notes:        draft.notes,
      customerNotes: draft.notes,
      serviceMonth: draft.serviceMonth,
      serviceMonthCustomFieldId: draft.serviceMonthCustomFieldId,
      draftLineItems: draft.lineItems.map(li => ({
        name:        li.name,
        description: li.description,
        quantity:    li.quantity,
        rate:        li.rate,
        item_id:     li.item_id,
        tax_id:      li.tax_id,
        tax_name:    li.tax_name,
        discount:    li.discount,
        discountType: li.discountType,
      })),
    }, order.salesorder_id)
      .then(() => loadOrder())
      .catch((err) => {
        console.error('[ServiceOrderDetail] update failed', err);
        presentAlert({
          header: tr('pages.ServiceOrderDetail.updateFailed'),
          message: err instanceof Error ? err.message : tr('pages.ServiceOrderDetail.couldNotSaveChangesToThis'),
          cssClass: 'glass-alert',
          buttons: ['OK'],
        });
      })
      .finally(() => setSavingEdit(false));
  }, [order, loadOrder, presentAlert, tr]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuBtnRef.current?.contains(t)) return;
      if (menuCardRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [menuOpen]);

  const toggleMenu = useCallback(() => {
    setMenuOpen(o => !o);
  }, []);

  const fetchOrderPdfBase64 = useCallback(async (): Promise<string | null> => {
    if (!order) return null;
    const zohoInvoicing = httpsCallable(getFunctions(), 'zohoInvoicing');
    const result = await zohoInvoicing({ action: 'salesOrderPdf', salesOrderId: order.salesorder_id });
    return (result.data as { pdf?: string })?.pdf ?? null;
  }, [order]);

  const handlePreview = useCallback(async () => {
    setShowPdfPreview(true);
    setPdfPreviewError(null);
    setPdfPreviewDataUrl(null);
    setPdfBusy('preview');
    try {
      const pdf = await fetchOrderPdfBase64();
      if (pdf) {
        setPdfPreviewDataUrl(`data:application/pdf;base64,${pdf}`);
      } else {
        setPdfPreviewError(tr('pages.ServiceOrderDetail.serviceOrderPdfIsNotAvailable'));
      }
    } catch (err) {
      console.error('[ServiceOrderDetail] preview failed', err);
      setPdfPreviewError(tr('pages.ServiceOrderDetail.unableToLoadTheServiceOrder'));
    } finally {
      setPdfBusy(null);
    }
  }, [fetchOrderPdfBase64, tr]);

  const handleDownloadPdf = useCallback(async () => {
    setPdfBusy('download');
    try {
      const pdf = await fetchOrderPdfBase64();
      if (!pdf) return;
      const byteChars = atob(pdf);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${order?.salesorder_number || 'service-order'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[ServiceOrderDetail] download failed', err);
    } finally {
      setPdfBusy(null);
    }
  }, [fetchOrderPdfBase64, order]);

  const handlePrint = useCallback(async () => {
    setPdfBusy('print');
    try {
      const pdf = await fetchOrderPdfBase64();
      if (!pdf) return;
      const win = window.open(`data:application/pdf;base64,${pdf}`, '_blank');
      // Best-effort — most browsers' built-in PDF viewer has its own print button;
      // programmatic print() on a data: URL frequently no-ops silently, so this
      // is a convenience attempt, not the only way to print from here.
      setTimeout(() => { try { win?.print(); } catch { /* ignore */ } }, 600);
    } catch (err) {
      console.error('[ServiceOrderDetail] print failed', err);
    } finally {
      setPdfBusy(null);
    }
  }, [fetchOrderPdfBase64]);

  // salesorder_id (not reference_number, empty by design on a standalone
  // order) is what actually identifies the order — see handleEditConfirm's
  // comment for the full explanation of this pattern.
  const canCancelNow = !!order?.salesorder_id
    && !['void', 'cancelled'].includes(String(order?.status || '').toLowerCase());
  const isDraft = String(order?.status || '').toLowerCase() === 'draft';

  const menuItems: Array<{ label: string; onClick?: () => void; disabled?: boolean; danger?: boolean }> = [
    ...(isDraft ? [{ label: tr('pages.ServiceOrderDetail.markAsConfirmed'), onClick: handleConfirmOrder, disabled: confirmingOrder }] : []),
    { label: tr('pages.ServiceOrderDetail.createInstantInvoice'), onClick: handleCreateInstantInvoice, disabled: creatingInvoice },
    { label: tr('pages.ServiceOrderDetail.convertToInvoice'), onClick: handleConvertToInvoice, disabled: creatingInvoice },
    { label: tr('pages.ServiceOrderDetail.void'), onClick: handleCancelOrder, disabled: !canCancelNow || cancelling },
    { label: tr('pages.ServiceOrderDetail.preview'), onClick: handlePreview, disabled: pdfBusy !== null },
    { label: tr('pages.ServiceOrderDetail.downloadPdf'), onClick: handleDownloadPdf, disabled: pdfBusy !== null },
    { label: tr('pages.ServiceOrderDetail.print'), onClick: handlePrint, disabled: pdfBusy !== null },
    { label: tr('pages.ServiceOrderDetail.clone'), onClick: handleCloneOrder, disabled: cloning },
    { label: tr('pages.ServiceOrderDetail.delete'), onClick: handleDeleteOrder, disabled: deleting, danger: true },
  ];

  if (isLoading) {
    const loadingContent = (
      <IonContent className={styles.loadingContent}>
        <div className={styles.loadingContainer}>
          <IonSpinner name="bubbles" color="primary" />
          <IonText>{tr('pages.ServiceOrderDetail.loadingServiceOrder')}</IonText>
        </div>
      </IonContent>
    );
    return onClose
      ? <div className={styles.modalWrapper}>{loadingContent}</div>
      : <IonPage className={styles.page}>{loadingContent}</IonPage>;
  }

  if (error || !order) {
    const errorContent = (
      <IonContent className={styles.errorContent}>
        <div className={styles.errorContainer}>
          <IonText color="danger">
            <h2>{tr('pages.ServiceOrderDetail.errorLoadingServiceOrder')}</h2>
            <p>{error || tr('pages.ServiceOrderDetail.noServiceOrderDataAvailable')}</p>
          </IonText>
          <IonButton fill="outline" onClick={handleClose}>{tr('pages.ServiceOrderDetail.back')}</IonButton>
        </div>
      </IonContent>
    );
    return onClose
      ? <div className={styles.modalWrapper}>{errorContent}</div>
      : <IonPage className={styles.page}>{errorContent}</IonPage>;
  }

  const statusClass = styles[STATUS_STYLE_MAP[String(order.status || '').toLowerCase()] ?? 'statusDraft'];

  const ba = order.billing_address;
  const billingAddressLines = [
    ba?.attention,
    ba?.address ?? ba?.street,
    ba?.street2,
    [ba?.city, ba?.state ?? ba?.state_code, ba?.zip].filter(Boolean).join(', ') || undefined,
    ba?.country,
    ba?.phone,
  ].filter((line): line is string => !!line && line.trim().length > 0);

  const fmtDate = (d?: string) => {
    if (!d || d.startsWith('0000')) return '—';
    const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
    return dt.toLocaleDateString(intlLocale(), { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  const editDraft: ServiceOrderDraft = {
    soNumber:           order.salesorder_number,
    soDate:             order.date,
    paymentTerms:       order.payment_terms,
    paymentTermsLabel:  order.payment_terms_label,
    serviceMonth:       order.service_month,
    isInclusiveTax:     order.is_inclusive_tax ?? false,
    pricelistId:        order.pricelist_id,
    lineItems: (order.line_items ?? []).map(li => ({
      item_id:      li.item_id || undefined,
      name:         li.name,
      description:  li.description || '',
      quantity:     li.quantity,
      rate:         li.rate,
      discount:     li.discount,
      discountType: li.discount_type === '$' ? '$' : '%',
      tax_id:       li.tax_id || undefined,
      tax_name:     li.tax_name || undefined,
    })),
    notes:  order.customer_notes || '',
    terms:  order.terms,
    total:  order.total,
    taxAmount: order.tax_total ?? 0,
    action: 'confirm',
  };

  const pageContent = (
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={tr('pages.ServiceOrderDetail.serviceOrder')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        onClose ? (
          <IonButton shape='round' className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        ) : (
          <IonButton shape='round' className={styles.backButton} onClick={handleClose}>
            <IonIcon icon={chevronBack} />
          </IonButton>
        )
      }
      endButtons={
        <>
          <IonButton shape="round" className={styles.headerIconButton} disabled={savingEdit} onClick={() => setShowEdit(true)}>
            <IonIcon icon={createOutline} />
          </IonButton>
          <IonButton shape="round" className={styles.headerIconButton} onClick={() => setShowEmail(true)}>
            <IonIcon icon={mailOutline} />
          </IonButton>
          <IonButton shape="round" className={styles.headerIconButton} ref={menuBtnRef} onClick={toggleMenu}>
            <IonIcon icon={ellipsisVertical} />
          </IonButton>
        </>
      }
    >
      <IonContent scrollY={false} className={styles.content}>
        <PositionedRefresher
          titleRef={titleCardElementRef}
          contentRef={contentElementRef}
          offset={0}
          anchor="bottom"
          id="service-order-refresher"
          pullMin={120}
          pullMax={200}
          pullFactor={0.6}
          disabled={refresherDisabled}
          onIonRefresh={(e) => {
            const doRefresh = async () => {
              try { await loadOrder(); } finally { e.detail.complete(); }
            };
            doRefresh();
          }}
          style={{
            '--ion-color-step-250': 'var(--ion-background-color)',
            '--ion-color-step-200': 'var(--ion-background-color)',
            '--ion-background-color': 'transparent',
          } as React.CSSProperties}
        >
          <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
        </PositionedRefresher>
        <IonGrid
          ref={contentElementRef}
          className={styles.invoiceContainer}
          onScroll={(e) => {
            const top = (e.target as HTMLElement).scrollTop || 0;
            const atTop = top <= 5;
            if (atTop !== wasAtTop.current) {
              wasAtTop.current = atTop;
              setRefresherDisabled(!atTop);
            }
            handleScroll(top);
          }}
        >
          <div ref={titleCardElementRef}>
            <TitleCard
              ref={titleCardAnimationRef}
              title={tr('pages.ServiceOrderDetail.serviceOrder')}
              cardClassName={styles.invoiceTitleCard}
              titleContainerClassName={styles.titleContainer}
              titleClassName={styles.invoicePageTitle}
            >
              <div className={styles.invoiceHeader}>
                <div className={styles.companySection}>
                  <div className={styles.companyLogoWrapper}>
                    <img
                      src="/assets/icon/maskable_icon_192.png"
                      alt={tr('pages.ServiceOrderDetail.rSMaidsService')}
                      className={styles.companyLogoImg}
                    />
                  </div>
                  <h1 className={styles.companyName}>{BUSINESS_FALLBACK.name}</h1>
                </div>

                <div className={styles.topSection}>
                  <div className={styles.invoiceDetailsColumn}>
                    <div className={styles.statusRow}>
                      <span className={styles.invoiceNumber}>{order.salesorder_number}</span>
                      <span className={statusClass}>
                        {(order.status || 'draft').charAt(0).toUpperCase() + (order.status || 'draft').slice(1)}
                      </span>
                    </div>

                    <h3 className={styles.columnTitle}>{tr('pages.ServiceOrderDetail.orderDetails')}</h3>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{tr('pages.ServiceOrderDetail.orderDate')}</span>
                      <span className={styles.detailValue}>{fmtDate(order.date)}</span>
                    </div>
                    {order.payment_terms_label && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{tr('pages.ServiceOrderDetail.paymentTerms')}</span>
                        <span className={styles.detailValue}>{order.payment_terms_label}</span>
                      </div>
                    )}
                    {order.reference_number && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{tr('pages.ServiceOrderDetail.bookingRef')}</span>
                        <span className={styles.detailValue}>{order.reference_number}</span>
                      </div>
                    )}
                    {!!order.invoices?.length && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>{tr('pages.ServiceOrderDetail.invoicedAs')}</span>
                        <span className={styles.detailValue}>{order.invoices.map(i => i.invoice_number).join(', ')}</span>
                      </div>
                    )}
                  </div>

                  <div className={styles.accountColumn}>
                    <h3 className={styles.columnTitle}>{tr('pages.ServiceOrderDetail.billTo')}</h3>
                    <p className={styles.billingName}>{order.customer_name ?? '—'}</p>
                    <div className={styles.billingAddress}>
                      {billingAddressLines.map((line, index) => (
                        <p key={`${line}-${index}`}>{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TitleCard>
          </div>

          {/* Items Table */}
          <section className={styles.itemsSection}>
            <table className={styles.itemsTable}>
              <thead>
                <tr>
                  <th className={styles.itemHeader}>{tr('pages.ServiceOrderDetail.item')}</th>
                  <th className={styles.qtyHeader}>{tr('pages.ServiceOrderDetail.qty')}</th>
                  <th className={styles.priceHeader}>{tr('pages.ServiceOrderDetail.rate')}</th>
                  <th className={styles.taxHeader}>{tr('pages.ServiceOrderDetail.tax')}</th>
                  <th className={styles.totalHeader}>{tr('pages.ServiceOrderDetail.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {(order.line_items ?? []).map((l, i) => {
                  const isExpanded = expandedRows.has(i);
                  const hasDesc = !!(l.description && l.name);
                  return (
                    <tr key={i} className={styles.itemRow}>
                      <td className={styles.itemCell}>
                        <div className={styles.itemName}>{l.name ?? l.description}</div>
                        {hasDesc && (
                          <>
                            <div className={`${styles.itemDescription} ${isExpanded ? styles.itemDescExpanded : ''}`}>
                              {l.description}
                            </div>
                            <button className={styles.expandBtn} onClick={() => toggleRow(i)}>
                              {isExpanded ? tr('pages.ServiceOrderDetail.showLess') : tr('pages.ServiceOrderDetail.showMore')}
                            </button>
                          </>
                        )}
                      </td>
                      <td className={styles.qtyCell}>{l.quantity}</td>
                      <td className={styles.priceCell}>{fmt(l.rate)}</td>
                      <td className={styles.taxCell}>{l.tax_name || '—'}</td>
                      <td className={styles.totalCell}>{fmt(l.item_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          {/* Comments Section */}
          {order.customer_notes && (
            <section className={styles.commentsSection}>
              <h3 className={styles.commentsTitle}>{tr('pages.ServiceOrderDetail.comments')}</h3>
              <div className={styles.commentsText}>{order.customer_notes}</div>
            </section>
          )}

          {/* Totals Section */}
          <section className={styles.totalsSection}>
            <div className={styles.totalsGrid}>
              {order.sub_total != null && (
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>{tr('pages.ServiceOrderDetail.subtotal')}{order.is_inclusive_tax ? tr('pages.ServiceOrderDetail.taxInclusive') : ''}</span>
                  <span className={styles.totalValue}>{fmt(order.sub_total)}</span>
                </div>
              )}
              {!!order.discount_total && order.discount_total > 0 && (
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>{tr('pages.ServiceOrderDetail.discount')}</span>
                  <span className={styles.totalValue}>-{fmt(order.discount_total)}</span>
                </div>
              )}
              {(order.taxes ?? []).map((tax, i) => (
                <div key={i} className={styles.totalRow}>
                  <span className={styles.totalLabel}>
                    {tax.tax_name}{tax.tax_percentage != null ? ` (${tax.tax_percentage}%)` : ''}
                  </span>
                  <span className={styles.totalValue}>{tax.tax_amount_formatted ?? fmt(tax.tax_amount)}</span>
                </div>
              ))}
              {!!order.adjustment && order.adjustment !== 0 && (
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>{tr('pages.ServiceOrderDetail.adjustment')}</span>
                  <span className={styles.totalValue}>{fmt(order.adjustment)}</span>
                </div>
              )}
              <div className={styles.totalRowFinal}>
                <span className={styles.totalLabelFinal}>{tr('pages.ServiceOrderDetail.total')}</span>
                <span className={styles.totalValueFinal}>{fmt(order.total)}</span>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className={styles.footer}>
            <p className={styles.footerTitle}>{tr('pages.ServiceOrderDetail.thankYouForYourBusiness')}</p>
            {order.terms && (
              <div className={styles.termsSection}>
                <p className={styles.termsTitle}>{tr('pages.ServiceOrderDetail.termsConditions')}</p>
                <p className={styles.footerText}>{order.terms}</p>
              </div>
            )}
            <p className={styles.footerContact}>{BUSINESS_FALLBACK.name} &bull; {BUSINESS_FALLBACK.address}</p>
            <p className={styles.footerContact}>Phone: {BUSINESS_FALLBACK.phone} &bull; {BUSINESS_FALLBACK.email}</p>
            <p className={styles.footerLink}>{BUSINESS_FALLBACK.website}</p>
          </footer>
        </IonGrid>
      </IonContent>
    </BasePageWithHeader>
  );

  const withModals = (
    <>
      {pageContent}

      <IonModal
        isOpen={showEmail}
        onDidDismiss={() => setShowEmail(false)}
        onWillDismiss={() => setShowEmail(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <EmailCompose
          entityType="salesorder"
          entityId={order.salesorder_id}
          entityNumber={order.salesorder_number}
          defaultTo={order.email ?? order.customer_email}
          onClose={() => setShowEmail(false)}
        />
      </IonModal>

      <ServiceOrderModal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        onConfirm={handleEditConfirm}
        serviceName=""
        customerName={order.customer_name}
        bookingId={order.reference_number}
        initialDraft={editDraft}
      />

      {/* A REAL nested <IonModal> — not a raw document.body portal. That was
          the actual bug behind the page-number input never taking focus:
          Ionic's modal focus-trap (active because this whole page already
          lives inside ServiceOrderList's own <IonModal>) doesn't recognize
          a plain portaled div as part of any tracked modal, so it yanked
          focus back to this page's own header close button on every tap
          inside the raw portal, no matter what was tapped — confirmed via
          `document.activeElement` in a live remote-debugging session.
          Nested IonModals are a supported Ionic pattern and correctly
          extend the focus-trap boundary to include this content. */}
      <IonModal
        isOpen={showPdfPreview}
        onDidDismiss={() => setShowPdfPreview(false)}
        onWillDismiss={() => setShowPdfPreview(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        <div className={menuStyles.pdfPreviewOverlay}>
          <div className={menuStyles.pdfPreviewHeader}>
            <span>{tr('pages.ServiceOrderDetail.preview')}</span>
            <button className={menuStyles.pdfPreviewClose} onClick={() => setShowPdfPreview(false)}>
              <IonIcon icon={closeOutline} />
            </button>
          </div>
          <div className={menuStyles.pdfPreviewWrap}>
            {pdfPreviewError ? (
              <div className={styles.errorContainer}>
                <IonText color="danger"><p>{pdfPreviewError}</p></IonText>
              </div>
            ) : (
              <PdfViewerNative
                dataUrl={pdfPreviewDataUrl}
                isLoading={pdfBusy === 'preview'}
                fileName={`${order.salesorder_number || 'service-order'}.pdf`}
                onZoomChange={setPdfZoom}
                status={order.status}
              />
            )}
          </div>
        </div>
      </IonModal>

      <IonModal
        isOpen={showInvoiceId !== null}
        onDidDismiss={() => setShowInvoiceId(null)}
        onWillDismiss={() => setShowInvoiceId(null)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
        className={styles.fullscreenModal}
      >
        {showInvoiceId && (
          <Invoice documentType="invoice" invoiceId={showInvoiceId} onClose={() => setShowInvoiceId(null)} />
        )}
      </IonModal>

      <IonModal
        isOpen={showConvertToInvoice}
        onDidDismiss={() => setShowConvertToInvoice(false)}
        canDismiss={true}
        backdropDismiss={true}
        handle={false}
        className={styles.fullscreenModal}
      >
        {showConvertToInvoice && (
          <CreateInvoice
            onClose={() => setShowConvertToInvoice(false)}
            onCreated={() => { setShowConvertToInvoice(false); loadOrder(); }}
            prefill={{
              invoiceType: 'INVOICE',
              customerName: order.customer_name,
              contactId: order.customer_id,
              linkedSalesOrderNumber: order.salesorder_number,
              lineItems: (order.line_items ?? []).map((li) => ({
                // Exact item name, nothing appended — see
                // CreateInvoicePrefillLineItem's doc comment for why.
                description: li.name,
                itemDescription: li.description,
                quantity: li.quantity,
                price: li.rate,
                discountAmount: li.discount ? String(li.discount) : '',
                taxRate: li.tax_percentage ? String(li.tax_percentage / 100) : '',
              })),
            }}
          />
        )}
      </IonModal>

      {menuOpen && createPortal(
        <div ref={menuCardRef} className={menuStyles.ellipsisMenu}>
          {menuItems.map((item) => (
            <button
              key={item.label}
              className={`${menuStyles.ellipsisMenuItem} ${item.danger ? menuStyles.ellipsisMenuItemDanger : ''}`}
              disabled={item.disabled}
              onClick={() => { setMenuOpen(false); item.onClick?.(); }}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );

  return onClose
    ? <div className={styles.modalWrapper}>{withModals}</div>
    : <IonPage className={styles.page}>{withModals}</IonPage>;
};

export default ServiceOrderDetail;
